import { useEffect, useState } from 'react';
import CdrAnalytics from './CdrAnalytics';
import CdrDashboard from './CdrDashboard';
import CdrNumbers from './CdrNumbers';
import CdrInsights from './CdrInsights';
import CdrAlerts from './CdrAlerts';
import CdrContactCenter from './CdrContactCenter';
import CdrRequest from './CdrRequest';
import CdrAudit from './CdrAudit';
import CdrStatements from './CdrStatements';
import CdrExports from './CdrExports';
import CdrReports from './CdrReports';
import CdrStatus from './CdrStatus';
import CdrCustomReport from './CdrCustomReport';
import CdrAiAgent from './CdrAiAgent';

type Theme = 'light' | 'dark' | 'system';
function applyTheme(t: Theme) {
  const dark = t === 'dark' || (t === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', dark);
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
}
function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => { try { return (localStorage.getItem('tnh-theme') as Theme) || 'system'; } catch { return 'system'; } });
  useEffect(() => {
    applyTheme(theme);
    try { localStorage.setItem('tnh-theme', theme); } catch {}
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const h = () => applyTheme('system'); mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, [theme]);
  const opts: { k: Theme; label: string; d: string }[] = [
    { k: 'light', label: 'Light', d: 'M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10z' },
    { k: 'dark', label: 'Dark', d: 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z' },
    { k: 'system', label: 'Auto', d: 'M3 4h18v12H3zM8 20h8M12 16v4' },
  ];
  return (
    <div>
      <div className="px-2 mb-1 text-[10px] uppercase tracking-[0.14em] text-gray-400 font-semibold">Theme</div>
      <div className="flex gap-1 px-1">
        {opts.map((o) => (
          <button key={o.k} onClick={() => setTheme(o.k)} title={o.label}
            className={`flex-1 flex items-center justify-center gap-1 px-1.5 py-1 rounded-md text-xs transition ${theme === o.k ? 'bg-tnh-accent/10 text-tnh-accent font-medium' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">{o.d.split(/\s+M/).map((seg, i) => <path key={i} d={i === 0 ? seg : 'M' + seg} />)}</svg>
            <span>{o.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const NAV_ICONS: Record<string, string[]> = {
  search: ['M11 19a8 8 0 100-16 8 8 0 000 16z', 'M21 21l-4.35-4.35'],
  dashboard: ['M3 3h7v7H3z', 'M14 3h7v7h-7z', 'M14 14h7v7h-7z', 'M3 14h7v7H3z'],
  numbers: ['M4 9h16', 'M4 15h16', 'M10 3L8 21', 'M16 3l-2 18'],
  insights: ['M12 20V10', 'M18 20V4', 'M6 20v-4'],
  alerts: ['M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 01-3.46 0'],
  analytics: ['M22 12h-4l-3 9L9 3l-3 9H2'],
  cc: ['M3 18v-5a9 9 0 0118 0v5', 'M21 19a2 2 0 01-2 2h-2v-6h2a2 2 0 012 2z', 'M3 19a2 2 0 002 2h2v-6H5a2 2 0 00-2 2z'],
  aiagent: ['M12 8V4H8', 'M4 8h16v12H4z', 'M2 14h2', 'M20 14h2', 'M9 13v2', 'M15 13v2'],
  request: ['M22 12h-6l-2 3h-4l-2-3H2', 'M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z'],
  audit: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
  statements: ['M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8', 'M10 9H8'],
  exports: ['M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3'],
  directory: ['M4 19.5A2.5 2.5 0 016.5 17H20', 'M4 19.5A2.5 2.5 0 006.5 22H20V2H6.5A2.5 2.5 0 004 4.5v15z'],
  rates: ['M12 1v22', 'M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6'],
  access: ['M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2', 'M9 11a4 4 0 100-8 4 4 0 000 8z', 'M23 21v-2a4 4 0 00-3-3.87', 'M16 3.13a4 4 0 010 7.75'],
  bot: ['M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z'],
  reports: ['M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4', 'M17 8l-5-5-5 5', 'M12 3v12'],
  status: ['M22 12h-4l-3 9L9 3l-3 9H2'],
  customreport: ['M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z', 'M14 2v6h6', 'M12 18v-4', 'M9 15h6'],
  signout: ['M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4', 'M16 17l5-5-5-5', 'M21 12H9'],
};
function NavIcon({ k }: { k: string }) {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" className="shrink-0">{(NAV_ICONS[k] || []).map((p, i) => <path key={i} d={p} />)}</svg>;
}

type Row = {
  date: string; time: string; direction: string; department: string;
  caller_number: string; called_number: string;
  user_name: string; user_email: string; duration: string;
  cost: number | null; rate_tier: string; billed_min: number;
  flow: string; public_safety: number;
  outcome: string; intl: number;
};
type Totals = { total_cost: number; billed_calls: number; billed_minutes: number; currency: string };

const SORT_COLS: { k: string; label: string; right?: boolean }[] = [
  { k: 'date', label: 'Date' }, { k: 'time', label: 'Time' },
  { k: 'direction', label: 'Direction' }, { k: 'flow', label: 'Flow' }, { k: 'outcome', label: 'Outcome' }, { k: 'department', label: 'Dept / BU' },
  { k: 'caller_number', label: 'Caller Number' }, { k: 'called_number', label: 'Called Number' },
  { k: 'user_name', label: 'User Name' }, { k: 'user_email', label: 'User Email' },
  { k: 'duration', label: 'Duration', right: true }, { k: 'rate_tier', label: 'Rate' }, { k: 'cost', label: 'Cost', right: true },
];
function durationSec(s: string): number { if (!s) return 0; let t = 0; const h = s.match(/(\d+)\s*h/), m = s.match(/(\d+)\s*m/), x = s.match(/(\d+)\s*s/); if (h) t += +h[1] * 3600; if (m) t += +m[1] * 60; if (x) t += +x[1]; return t; }
function sortVal(r: Row, k: string): string | number {
  if (k === 'date' || k === 'time') return `${r.date} ${r.time}`;
  if (k === 'duration') return durationSec(r.duration);
  if (k === 'cost') return r.cost == null ? -1 : r.cost;
  return String((r as any)[k] ?? '').toLowerCase();
}
type Rate = { tier: string; label: string; rate_per_min: number };
type DeptRule = { id: string; match_type: string; match_value: string; department: string; priority: number };

const FLOW_LABELS: Record<string,string> = { contact_center:'Contact Center', call_queue:'Call Queue', hunt_group:'Hunt Group', auto_attendant:'Auto Attendant', voicemail:'Voicemail', direct:'Direct' };
function flowLabel(f: string){ return FLOW_LABELS[f] || 'Direct'; }
function outcomeBadge(o: string){
  if (o === 'answered') return <span className="px-1.5 py-0.5 rounded bg-green-600/15 text-green-700 dark:text-green-400 text-[11px] font-medium">Answered</span>;
  if (o === 'missed') return <span className="px-1.5 py-0.5 rounded bg-gray-500/15 text-gray-600 dark:text-gray-300 text-[11px] font-medium">Missed</span>;
  if (o === 'abandoned') return <span className="px-1.5 py-0.5 rounded bg-orange-600/15 text-orange-700 dark:text-orange-400 text-[11px] font-medium">Abandoned</span>;
  return <span className="text-gray-400">—</span>;
}

export default function CdrSearch({ fullHeight = false }: { fullHeight?: boolean }) {
  const [number, setNumber] = useState('');
  const [user, setUser] = useState('');
  const [direction, setDirection] = useState('');
  const [dept, setDept] = useState('');
  const [flow, setFlow] = useState('');
  const [outcome, setOutcome] = useState('');
  const [intlOnly, setIntlOnly] = useState(false);
  const [oneRow, setOneRow] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [role, setRole] = useState('');
  const [who, setWho] = useState('');
  const [tenant, setTenant] = useState('');
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [colFilters, setColFilters] = useState<Record<string, string>>({});
  const [winNote, setWinNote] = useState<string | null>(null);
  const isAdmin = role === 'tenant_admin' || role === 'super_admin';
  const [mode, setMode] = useState<'search' | 'dashboard' | 'numbers' | 'insights' | 'alerts' | 'cc' | 'aiagent' | 'analytics' | 'request' | 'audit' | 'statements' | 'exports' | 'status' | 'customreport'>('dashboard');
  async function signOut() {
    try { await fetch('/cdr/auth/logout', { method: 'GET', credentials: 'include' }); } catch {}
    window.location.reload();
  }
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem('cdr-nav-collapsed') === '1'; } catch { return false; } });
  const toggleNav = () => setCollapsed(c => { try { localStorage.setItem('cdr-nav-collapsed', c ? '0' : '1'); } catch {} return !c; });

  const [showRates, setShowRates] = useState(false);
  const [rates, setRates] = useState<Rate[]>([]);
  const [localNpas, setLocalNpas] = useState('');
  const [rateMsg, setRateMsg] = useState<string | null>(null);

  const [showDepts, setShowDepts] = useState(false);
  const [departments, setDepartments] = useState<string[]>([]);
  const [deptRules, setDeptRules] = useState<DeptRule[]>([]);
  const [nr, setNr] = useState({ match_type: 'email', match_value: '', department: '', priority: 100 });
  const [deptMsg, setDeptMsg] = useState<string | null>(null);
  const [showBot, setShowBot] = useState(false);
  const [botUsers, setBotUsers] = useState<{email:string;role:string}[]>([]);
  const [newBot, setNewBot] = useState({ email: '', role: 'user' });
  const [botSetupMsg, setBotSetupMsg] = useState<string | null>(null);
  const [botTokenInput, setBotTokenInput] = useState('');
  const [otpKeyInput, setOtpKeyInput] = useState('');
  const [otpMsg, setOtpMsg] = useState<string | null>(null);
  const [ingestMsg, setIngestMsg] = useState<string | null>(null);
  const [ingestBusy, setIngestBusy] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [cdrUsers, setCdrUsers] = useState<{email:string;role:string}[]>([]);
  const [newAccess, setNewAccess] = useState({ email: '', role: 'viewer' });
  const [accessMsg, setAccessMsg] = useState<string | null>(null);
  const [botMsg, setBotMsg] = useState<string | null>(null);

  useEffect(() => {
    fetch('/cdr/api/whoami', { credentials: 'include' }).then(r => r.json()).then(j => { setRole(j.role || ''); setWho(j.who || ''); setTenant(j.tenant || ''); }).catch(() => {});
    loadDepts();
  }, []);

  function qs() {
    const p: Record<string, string> = { limit: '2000' };
    if (number) p.number = number;
    if (user) p.user = user;
    if (direction) p.direction = direction;
    if (dept) p.dept = dept;
    if (flow === 'public_safety') p.ps = '1'; else if (flow) p.flow = flow;
    if (outcome) p.outcome = outcome;
    if (intlOnly) p.intl = '1';
    p.dedupe = oneRow ? '1' : '0';
    if (from) p.from = from;
    if (to) p.to = to;
    return new URLSearchParams(p).toString();
  }
  async function run() {
    setBusy(true); setErr(null);
    try {
      const r = await fetch('/cdr/api/search?' + qs(), { credentials: 'include' });
      if (r.status === 401) { setErr('Not authorized to view CDR — ask an admin to add you to the allowlist.'); setRows([]); setTotals(null); return; }
      if (!r.ok) { setErr('Search failed (' + r.status + ')'); setRows([]); setTotals(null); return; }
      const j = await r.json(); setRows(j.results || []); setTotals(j.totals || null); setSearched(true);
      setWinNote(j.default_window === '30d' ? 'No filter set — showing the last 30 days (up to 2000 calls). Add a number, user, or date to search further back.' : null);
    } catch (e: any) { setErr(e?.message ?? 'search failed'); } finally { setBusy(false); }
  }
  async function exportCsv() {
    try {
      const r = await fetch('/cdr/api/export?' + qs(), { credentials: 'include' });
      if (!r.ok) { setErr('Export failed (' + r.status + ')'); return; }
      const j = await r.json(); window.location.href = j.download;
    } catch (e: any) { setErr(e?.message ?? 'export failed'); }
  }
  async function loadRates() { const r = await fetch('/cdr/api/rates', { credentials: 'include' }); if (r.ok) { const j = await r.json(); setRates(j.rates || []); setLocalNpas(j.local_npas || ''); } }
  async function saveRates() {
    setRateMsg(null);
    const r = await fetch('/cdr/api/rates', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ rates, local_npas: localNpas }) });
    setRateMsg(r.ok ? 'Saved.' : r.status === 403 ? 'Only tenant admins can edit rates.' : 'Save failed (' + r.status + ').');
    if (r.ok) loadRates();
  }
  async function loadDepts() { const r = await fetch('/cdr/api/departments', { credentials: 'include' }); if (r.ok) { const j = await r.json(); setDepartments(j.departments || []); setDeptRules(j.rules || []); } }
  async function saveRule() {
    setDeptMsg(null);
    if (!nr.match_value || !nr.department) { setDeptMsg('Enter a value and a department.'); return; }
    const r = await fetch('/cdr/api/departments', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify(nr) });
    if (r.ok) { setNr({ match_type: 'email', match_value: '', department: '', priority: 100 }); setDeptMsg('Added.'); loadDepts(); }
    else setDeptMsg(r.status === 403 ? 'Only tenant admins can edit the directory.' : 'Save failed (' + r.status + ').');
  }
  async function delRule(id: string) {
    const r = await fetch('/cdr/api/departments', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ id, _delete: true }) });
    if (r.ok) loadDepts();
  }
  async function loadBotUsers() { const r = await fetch('/cdr/api/botusers', { credentials: 'include' }); if (r.ok) setBotUsers((await r.json()).users || []); }
  async function saveBotUser() {
    setBotMsg(null);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newBot.email)) { setBotMsg('Enter a valid email address.'); return; }
    try {
      const r = await fetch('/cdr/api/botusers', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify(newBot) });
      if (r.ok) { setNewBot({ email: '', role: 'user' }); setBotMsg('Added ✓'); loadBotUsers(); }
      else setBotMsg(r.status === 403 ? 'Only admins can manage bot access.' : 'Add failed (' + r.status + ').');
    } catch (e: any) { setBotMsg('Error: ' + (e?.message || e)); }
  }
  async function delBotUser(email: string) {
    setBotMsg(null);
    try { const r = await fetch('/cdr/api/botusers', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email, _delete: true }) }); if (r.ok) { setBotMsg('Removed ✓'); loadBotUsers(); } else setBotMsg('Remove failed (' + r.status + ').'); }
    catch (e: any) { setBotMsg('Error: ' + (e?.message || e)); }
  }
  async function loadUsers() { const r = await fetch('/cdr/api/users', { credentials: 'include' }); if (r.ok) setCdrUsers((await r.json()).users || []); }
  async function saveUser() {
    setAccessMsg(null);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newAccess.email)) { setAccessMsg('Enter a valid email address.'); return; }
    try {
      const r = await fetch('/cdr/api/users', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify(newAccess) });
      if (r.ok) { setNewAccess({ email: '', role: 'viewer' }); setAccessMsg('Added ✓'); loadUsers(); }
      else setAccessMsg(r.status === 403 ? 'Only admins can manage access.' : 'Add failed (' + r.status + ').');
    } catch (e: any) { setAccessMsg('Error: ' + (e?.message || e)); }
  }
  async function delUser(email: string) {
    setAccessMsg(null);
    try { const r = await fetch('/cdr/api/users', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email, _delete: true }) }); if (r.ok) { setAccessMsg('Removed ✓'); loadUsers(); } else setAccessMsg('Remove failed (' + r.status + ').'); }
    catch (e: any) { setAccessMsg('Error: ' + (e?.message || e)); }
  }
  async function saveBotToken() {
    if (!botTokenInput.trim()) { setBotSetupMsg('Paste the bot access token first.'); return; }
    setBotSetupMsg('Saving & registering…');
    try {
      const r = await fetch('/cdr/api/botsecret', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ token: botTokenInput.trim() }) });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.ok) { setBotSetupMsg('Saved & registered ✓ Bot: ' + (j.bot_email || '(unknown)') + '. Message it to test.'); setBotTokenInput(''); }
      else setBotSetupMsg('Failed: ' + (j.error || ('status ' + r.status)));
    } catch (e: any) { setBotSetupMsg('Error: ' + (e?.message || e)); }
  }
  async function runIngest() {
    setIngestBusy(true); setIngestMsg('Pulling from Webex…');
    try {
      const r = await fetch('/cdr/api/ingestnow', { method: 'POST', credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (r.ok && !j.error) setIngestMsg(`Pulled ${j.inserted} rows · ${j.windows} windows · now at ${String(j.checkpoint || '').replace('T', ' ').slice(0, 16)} UTC${j.rateLimited ? ' · rate-limited, click again to continue' : ''}`);
      else setIngestMsg('Failed: ' + (j.error || ('status ' + r.status)));
    } catch (e: any) { setIngestMsg('Error: ' + (e?.message || e)); } finally { setIngestBusy(false); }
  }
  async function saveOtpKey() {
    if (!otpKeyInput.trim()) { setOtpMsg('Paste the Resend key first.'); return; }
    setOtpMsg('Saving & sending test code…');
    try {
      const r = await fetch('/cdr/api/otpsecret', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ key: otpKeyInput.trim() }) });
      const j = await r.json().catch(() => ({}));
      setOtpMsg(r.ok && j.ok ? ('Saved ✓ test code sent to ' + (j.sent_to || 'you')) : ('Failed: ' + (j.note || j.error || ('status ' + r.status))));
      if (r.ok && j.ok) setOtpKeyInput('');
    } catch (e: any) { setOtpMsg('Error: ' + (e?.message || e)); }
  }
  async function registerBot() {
    setBotSetupMsg('Re-registering…');
    try {
      const r = await fetch('/cdr/api/botsetup', { method: 'POST', credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      setBotSetupMsg(r.ok && j.ok ? ('Registered ✓ Bot: ' + (j.bot_email || '(unknown)')) : ('Failed: ' + (j.error || ('status ' + r.status))));
    } catch (e: any) { setBotSetupMsg('Error: ' + (e?.message || e)); }
  }

  const inp = 'px-2.5 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs';
  const money = (n: number | null | undefined) => (n == null ? '—' : '$' + Number(n).toFixed(2));
  // 3-state column sort: asc -> desc -> off (off = server order, newest first).
  const toggleSort = (key: string) => setSort((s) => (s && s.key === key ? (s.dir === 'asc' ? { key, dir: 'desc' } : null) : { key, dir: 'asc' }));
  // per-column text filter (client-side, case-insensitive substring) — combines with sort.
  const colText = (r: Row, k: string): string => {
    if (k === 'flow') return flowLabel(r.flow);
    if (k === 'outcome') return r.outcome || '';
    if (k === 'cost') return r.cost == null ? '' : String(r.cost);
    return String((r as any)[k] ?? '');
  };
  const activeFilters = Object.entries(colFilters).filter(([, v]) => v && v.trim());
  const filteredRows = activeFilters.length
    ? rows.filter((r) => activeFilters.every(([k, v]) => colText(r, k).toLowerCase().includes(v.trim().toLowerCase())))
    : rows;
  const sortedRows = sort
    ? [...filteredRows].sort((a, b) => { const x = sortVal(a, sort.key), y = sortVal(b, sort.key); const c = x < y ? -1 : x > y ? 1 : 0; return sort.dir === 'asc' ? c : -c; })
    : filteredRows;
  return (
    <div className={`md:flex md:gap-4 md:items-start${fullHeight ? ' md:h-screen md:overflow-hidden' : ''}`}>
      <aside className={`${collapsed ? 'md:w-14' : 'md:w-56'} md:shrink-0 mb-3 md:mb-0 transition-[width] duration-200 ${fullHeight ? 'md:sticky md:top-0 md:h-screen md:overflow-y-auto md:py-3 md:pr-1' : 'md:sticky md:top-4'}`}>
        <div className="mb-3 px-1 flex items-center justify-between gap-2">
          <div className={collapsed ? 'md:hidden' : ''}>
            <div className="text-base font-semibold">WxC &amp; WxCC CDR</div>
            <div className="text-[11px] text-gray-500">Call Detail Records · {tenant === 'longbeach' ? 'Long Beach' : 'Santa Monica'}</div>
          </div>
          <button onClick={toggleNav} title={collapsed ? 'Expand menu' : 'Collapse menu'} className="hidden md:inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 shrink-0">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">{collapsed ? <path d="M9 18l6-6-6-6" /> : <path d="M15 18l-6-6 6-6" />}</svg>
          </button>
        </div>
        <nav className="flex md:flex-col flex-wrap gap-1">
          {(([['dashboard', 'Dashboard'], ['search', 'WxC CDR Record'], ['numbers', 'Numbers'], ['insights', 'Insights'], ['alerts', 'Alerts'], ['cc', 'Contact Center'], ['aiagent', 'AI Agent'], ['analytics', 'Analytics'], ['customreport', 'Generate Custom Report'], ['request', 'Records request'], ['status', 'Status'], ...(isAdmin ? [['audit', 'Audit'], ['statements', 'Statements'], ['exports', 'Exports']] : [])]) as [any, string][]).map(([m, label]) => (
            <button key={m} onClick={() => setMode(m)} title={label} className={`flex items-center gap-2.5 md:w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${collapsed ? 'md:justify-center md:px-0' : ''} ${mode === m ? 'bg-tnh-accent text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
              <NavIcon k={m} /><span className={collapsed ? 'md:hidden' : ''}>{label}</span>
            </button>
          ))}
        </nav>
        <div className={`mt-4 mb-1 px-2 text-[10px] uppercase tracking-wide text-gray-400 ${collapsed ? 'md:hidden' : ''}`}>Admin</div>
        <nav className="flex md:flex-col flex-wrap gap-1">
          <button onClick={() => { setShowDepts(v => !v); }} title="Dept directory" className={`flex items-center gap-2.5 md:w-full text-left px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-xs ${collapsed ? 'md:justify-center md:px-0' : ''}`}><NavIcon k="directory" /><span className={collapsed ? 'md:hidden' : ''}>{showDepts ? 'Hide directory' : 'Dept directory'}</span></button>
          {isAdmin && <button onClick={() => { const n = !showRates; setShowRates(n); if (n && rates.length === 0) loadRates(); }} title="Billing rates" className={`flex items-center gap-2.5 md:w-full text-left px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-xs ${collapsed ? 'md:justify-center md:px-0' : ''}`}><NavIcon k="rates" /><span className={collapsed ? 'md:hidden' : ''}>{showRates ? 'Hide rates' : 'Billing rates'}</span></button>}
          {isAdmin && <button onClick={() => { const n = !showAccess; setShowAccess(n); if (n) loadUsers(); }} title="Users" className={`flex items-center gap-2.5 md:w-full text-left px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-xs ${collapsed ? 'md:justify-center md:px-0' : ''}`}><NavIcon k="access" /><span className={collapsed ? 'md:hidden' : ''}>{showAccess ? 'Hide users' : 'Users'}</span></button>}
          {isAdmin && <button onClick={() => { const n = !showBot; setShowBot(n); if (n) loadBotUsers(); }} title="Bot access" className={`flex items-center gap-2.5 md:w-full text-left px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-xs ${collapsed ? 'md:justify-center md:px-0' : ''}`}><NavIcon k="bot" /><span className={collapsed ? 'md:hidden' : ''}>{showBot ? 'Hide bot access' : 'Bot access'}</span></button>}
          {isAdmin && <button onClick={() => setShowReports(v => !v)} title="Reports import" className={`flex items-center gap-2.5 md:w-full text-left px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-xs ${collapsed ? 'md:justify-center md:px-0' : ''}`}><NavIcon k="reports" /><span className={collapsed ? 'md:hidden' : ''}>{showReports ? 'Hide reports import' : 'Reports import'}</span></button>}
        </nav>
        <div className={`mt-4 pt-3 border-t border-tnh-border dark:border-tnh-border-dark space-y-2 ${collapsed ? 'md:px-0' : ''}`}>
          {!collapsed && <ThemeToggle />}
          {!collapsed && who && (
            <div className="px-2 pt-1">
              <div className="text-xs font-medium truncate" title={who}>{who}</div>
              <div className="text-[11px] text-gray-500">{(role || 'user').replace('_', ' ')}{tenant ? ' · ' + tenant : ''}</div>
            </div>
          )}
          <button onClick={signOut} title="Sign out" className={`flex items-center gap-2.5 md:w-full text-left px-3 py-2 rounded-md text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${collapsed ? 'md:justify-center md:px-0' : ''}`}>
            <NavIcon k="signout" /><span className={collapsed ? 'md:hidden' : ''}>Sign out</span>
          </button>
        </div>
      </aside>
      <div className={`flex-1 min-w-0 space-y-3 ${fullHeight ? 'md:h-screen md:overflow-y-auto md:py-3 md:pr-2 pb-10' : ''}`}>

      {/* Admin panels — opened from the Admin menu; available on any tab, not part of the Records view */}
      {showReports && isAdmin && <CdrReports />}
      {showAccess && isAdmin && (
        <div className="card p-3 space-y-2">
          <div className="text-sm font-semibold">CDR Portal</div>
          <div className="flex flex-wrap gap-2 items-end">
            <input value={newAccess.email} onChange={(e) => setNewAccess({ ...newAccess, email: e.target.value })} placeholder="name@santamonica.gov" className={inp} />
            <select value={newAccess.role} onChange={(e) => setNewAccess({ ...newAccess, role: e.target.value })} className={inp}><option value="viewer">Viewer</option><option value="admin">Admin</option></select>
            <button onClick={saveUser} className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-xs">Add user</button>
            {accessMsg && <span className="text-xs text-gray-500 self-center">{accessMsg}</span>}
          </div>
          <table className="w-full text-xs"><thead className="text-gray-500"><tr><th className="text-left px-2 py-1">Email</th><th className="text-left px-2 py-1">Role</th><th></th></tr></thead>
            <tbody>
              {cdrUsers.length === 0 && <tr><td colSpan={3} className="px-2 py-3 text-center text-gray-500">No users yet.</td></tr>}
              {cdrUsers.map((u) => (
                <tr key={u.email} className="border-t border-tnh-border/40 dark:border-tnh-border-dark/40"><td className="px-2 py-1">{u.email}</td><td className="px-2 py-1">{u.role}</td><td className="px-2 py-1 text-right"><button onClick={() => delUser(u.email)} className="text-red-600 hover:underline">Remove</button></td></tr>
              ))}
            </tbody>
          </table>
          <div className="text-[11px] text-gray-400">Viewer = search & export. Admin = also manage rates, departments, access, and ingest.</div>
        </div>
      )}
      {showBot && isAdmin && (
        <div className="card p-3 space-y-2">
          <div className="text-sm font-semibold">Bot &amp; Embedded App Access <span className="text-gray-500 font-normal">— users of the Webex bot and pinned app tab</span></div>
          <div className="flex flex-wrap gap-2 items-end">
            <input value={newBot.email} onChange={(e) => setNewBot({ ...newBot, email: e.target.value })} placeholder="name@santamonica.gov" className={inp} />
            <select value={newBot.role} onChange={(e) => setNewBot({ ...newBot, role: e.target.value })} className={inp}><option value="user">User</option><option value="admin">Admin</option></select>
            <button onClick={saveBotUser} className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-xs">Add</button>
            {botMsg && <span className="text-xs text-gray-500 self-center">{botMsg}</span>}
          </div>
          <div className="flex flex-wrap gap-2 items-center pt-1 border-t border-tnh-border/40 dark:border-tnh-border-dark/40">
            <span className="text-xs text-gray-500">Bot token:</span>
            <input type="password" value={botTokenInput} onChange={(e) => setBotTokenInput(e.target.value)} placeholder="paste CSM‑CDR bot access token" className={inp + ' flex-1 min-w-[240px]'} autoComplete="off" />
            <button onClick={saveBotToken} className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-xs">Save token &amp; register</button>
            <button onClick={registerBot} className="px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-xs">Re‑register</button>
            {botSetupMsg && <span className="text-xs text-gray-500 self-center w-full">{botSetupMsg}</span>}
          </div>
          <div className="flex flex-wrap gap-2 items-center pt-1 border-t border-tnh-border/40 dark:border-tnh-border-dark/40">
            <span className="text-xs text-gray-500">Login email key:</span>
            <input type="password" value={otpKeyInput} onChange={(e) => setOtpKeyInput(e.target.value)} placeholder="Resend key for the standalone OTP (webexapi.com.co)" className={inp + ' flex-1 min-w-[240px]'} autoComplete="off" />
            <button onClick={saveOtpKey} className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-xs">Save &amp; test</button>
            {otpMsg && <span className="text-xs text-gray-500 self-center w-full">{otpMsg}</span>}
          </div>
          <div className="flex flex-wrap gap-2 items-center pt-1 border-t border-tnh-border/40 dark:border-tnh-border-dark/40">
            <span className="text-xs text-gray-500">Data ingest:</span>
            <button onClick={runIngest} disabled={ingestBusy} className="px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-xs disabled:opacity-50">{ingestBusy ? 'Pulling…' : 'Catch up ingest now'}</button>
            <span className="text-[11px] text-gray-400">pulls the next few days from Webex (repeat until current)</span>
            {ingestMsg && <span className="text-xs text-gray-500 self-center w-full">{ingestMsg}</span>}
          </div>
          <table className="w-full text-xs"><thead className="text-gray-500"><tr><th className="text-left px-2 py-1">Email</th><th className="text-left px-2 py-1">Role</th><th></th></tr></thead>
            <tbody>
              {botUsers.length === 0 && <tr><td colSpan={3} className="px-2 py-3 text-center text-gray-500">No bot users yet.</td></tr>}
              {botUsers.map((u) => (
                <tr key={u.email} className="border-t border-tnh-border/40 dark:border-tnh-border-dark/40"><td className="px-2 py-1">{u.email}</td><td className="px-2 py-1">{u.role}</td><td className="px-2 py-1 text-right"><button onClick={() => delBotUser(u.email)} className="text-red-600 hover:underline">Remove</button></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showDepts && (
        <div className="card p-3 space-y-2">
          <div className="text-sm font-semibold">Department Directory{!isAdmin && <span className="text-gray-500 font-normal"> — read only</span>}</div>
          <div className="text-[11px] text-gray-500">Map a user email (substring) or a number/extension prefix to a department. First match by priority wins. Webex does not tag departments, so this drives the Dept filter + column.</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500"><tr><th className="text-left px-2 py-1">Match</th><th className="text-left px-2 py-1">Value</th><th className="text-left px-2 py-1">Department</th><th className="text-right px-2 py-1">Priority</th><th></th></tr></thead>
              <tbody>
                {deptRules.length === 0 && <tr><td colSpan={5} className="px-2 py-3 text-center text-gray-500">No mappings yet.</td></tr>}
                {deptRules.map((r) => (
                  <tr key={r.id} className="border-t border-tnh-border/40 dark:border-tnh-border-dark/40">
                    <td className="px-2 py-1">{r.match_type}</td><td className="px-2 py-1 font-mono">{r.match_value}</td>
                    <td className="px-2 py-1">{r.department}</td><td className="px-2 py-1 text-right">{r.priority}</td>
                    <td className="px-2 py-1 text-right">{isAdmin && <button onClick={() => delRule(r.id)} className="text-tnh-err hover:underline">remove</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {isAdmin && (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5 items-end">
              <select value={nr.match_type} onChange={(e) => setNr({ ...nr, match_type: e.target.value })} className={inp}><option value="email">Email contains</option><option value="prefix">Number/ext prefix</option></select>
              <input value={nr.match_value} onChange={(e) => setNr({ ...nr, match_value: e.target.value })} placeholder={nr.match_type === 'email' ? 'e.g. police@santamonica' : 'e.g. 8 or 4588'} className={inp + ' font-mono'} />
              <input value={nr.department} onChange={(e) => setNr({ ...nr, department: e.target.value })} placeholder="Department name" className={inp} />
              <input type="number" value={nr.priority} onChange={(e) => setNr({ ...nr, priority: Number(e.target.value) })} placeholder="Priority" className={inp} />
              <div className="flex items-center gap-2"><button onClick={saveRule} className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-xs">Add mapping</button>{deptMsg && <span className="text-xs text-gray-500">{deptMsg}</span>}</div>
            </div>
          )}
        </div>
      )}

      {showRates && (
        <div className="card p-3 space-y-2">
          <div className="text-sm font-semibold">Billing Rates ($/min){!isAdmin && <span className="text-gray-500 font-normal"> — read only</span>}</div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            {rates.map((rt, i) => (
              <label key={rt.tier} className="text-xs"><span className="text-gray-500">{rt.label || rt.tier}</span>
                <input type="number" step="0.001" min="0" value={rt.rate_per_min} disabled={!isAdmin} onChange={(e) => { const v = [...rates]; v[i] = { ...rt, rate_per_min: Number(e.target.value) }; setRates(v); }} className={inp + ' w-full mt-1'} /></label>
            ))}
            <label className="text-xs"><span className="text-gray-500">Local area codes</span><input value={localNpas} disabled={!isAdmin} onChange={(e) => setLocalNpas(e.target.value)} className={inp + ' w-full mt-1'} placeholder="310,424" /></label>
          </div>
          <div className="text-[11px] text-gray-500">Outbound priced by destination; international inbound also priced; minutes round up. Toll‑free/local default $0.</div>
          {isAdmin && <div className="flex items-center gap-2"><button onClick={saveRates} className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-xs">Save rates</button>{rateMsg && <span className="text-xs text-gray-500">{rateMsg}</span>}</div>}
        </div>
      )}

      {mode === 'search' && (<>
      <div className="card p-3 grid gap-2 sm:grid-cols-3 lg:grid-cols-7 items-end">
        <input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="Number (caller or called)" className={inp + ' font-mono'} />
        <input value={user} onChange={(e) => setUser(e.target.value)} placeholder="User (email or name)" className={inp} />
        <select value={dept} onChange={(e) => setDept(e.target.value)} className={inp}>
          <option value="">Any dept / BU</option>{departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={direction} onChange={(e) => setDirection(e.target.value)} className={inp}>
          <option value="">Any direction</option><option value="Inbound">Inbound</option><option value="Outbound">Outbound</option>
        </select>
        <select value={flow} onChange={(e) => setFlow(e.target.value)} className={inp}>
          <option value="">Any call flow</option>
          <option value="contact_center">Contact Center (WxCC)</option>
          <option value="call_queue">Call Queue</option>
          <option value="hunt_group">Hunt Group</option>
          <option value="auto_attendant">Auto Attendant</option>
          <option value="voicemail">Voicemail</option>
          <option value="direct">Direct</option>
          <option value="public_safety">Public Safety (911)</option>
        </select>
        <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className={inp}>
          <option value="">Any outcome</option>
          <option value="answered">Answered</option>
          <option value="missed">Missed</option>
          <option value="abandoned">Abandoned</option>
        </select>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inp} />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inp} />
        <div className="flex gap-2">
          <button onClick={run} disabled={busy} className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-xs disabled:opacity-50">{busy ? 'Searching…' : 'Search'}</button>
          <button onClick={exportCsv} disabled={busy || rows.length === 0} className="px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-xs disabled:opacity-50">Export CSV</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-5 px-1 -mt-1">
        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 select-none">
          <input type="checkbox" checked={oneRow} onChange={(e) => setOneRow(e.target.checked)} /> One row per call
        </label>
        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 select-none">
          <input type="checkbox" checked={intlOnly} onChange={(e) => setIntlOnly(e.target.checked)} /> International only
        </label>
        <button type="button" onClick={() => setShowFilters((v) => !v)} className={`text-xs px-2 py-0.5 rounded-md border ${showFilters ? 'border-tnh-accent text-tnh-accent' : 'border-tnh-border dark:border-tnh-border-dark text-gray-600 dark:text-gray-300'}`}>
          {showFilters ? '▾ Column filters' : '▸ Column filters'}
        </button>
        {activeFilters.length > 0 && (
          <button type="button" onClick={() => setColFilters({})} className="text-xs px-2 py-0.5 rounded-md text-tnh-accent hover:underline">Clear filters ({activeFilters.length})</button>
        )}
      </div>

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="card p-3"><div className="text-xl font-semibold">{money(totals.total_cost)}</div><div className="text-[11px] text-gray-500">Total outbound cost</div></div>
          <div className="card p-3"><div className="text-xl font-semibold">{totals.billed_calls}</div><div className="text-[11px] text-gray-500">Billed calls</div></div>
          <div className="card p-3"><div className="text-xl font-semibold">{totals.billed_minutes}</div><div className="text-[11px] text-gray-500">Billed minutes</div></div>
          <div className="card p-3"><div className="text-xl font-semibold">{sortedRows.length}{activeFilters.length > 0 && <span className="text-xs text-gray-400"> / {rows.length}</span>}</div><div className="text-[11px] text-gray-500">Records{activeFilters.length > 0 ? ' (filtered)' : ''}</div></div>
        </div>
      )}

      {err && <div className="card p-3 text-sm text-tnh-err">{err}</div>}
      {winNote && !err && <div className="card p-2.5 text-xs text-gray-600 dark:text-gray-300 border-l-2 border-tnh-accent">{winNote}</div>}
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 uppercase tracking-wide">
            <tr className="border-b border-tnh-border dark:border-tnh-border-dark">
              {SORT_COLS.map((c) => (
                <th key={c.k} className={`${c.right ? 'text-right' : 'text-left'} px-4 py-2`}>
                  <button
                    type="button"
                    onClick={() => toggleSort(c.k)}
                    title="Click to sort ascending / descending"
                    className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-tnh-accent ${sort?.key === c.k ? 'text-tnh-accent font-semibold' : ''} ${c.right ? 'flex-row-reverse' : ''}`}
                  >
                    {c.label}
                    <span className="text-[10px] leading-none">{sort?.key === c.k ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
                  </button>
                </th>
              ))}
            </tr>
            {showFilters && (
              <tr className="border-b border-tnh-border dark:border-tnh-border-dark">
                {SORT_COLS.map((c) => (
                  <th key={c.k} className="px-2 py-1 font-normal normal-case">
                    <input
                      value={colFilters[c.k] || ''}
                      onChange={(e) => setColFilters((f) => ({ ...f, [c.k]: e.target.value }))}
                      placeholder="filter…"
                      className={`w-full min-w-[70px] px-1.5 py-1 rounded border border-tnh-border dark:border-tnh-border-dark bg-transparent text-[11px] ${c.right ? 'text-right' : ''}`}
                    />
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={13} className="text-center text-gray-500 py-8">{searched ? 'No matching calls.' : 'Enter a filter and press Search.'}</td></tr>}
            {rows.length > 0 && sortedRows.length === 0 && <tr><td colSpan={13} className="text-center text-gray-500 py-8">No rows match the column filters. <button type="button" onClick={() => setColFilters({})} className="text-tnh-accent hover:underline">Clear filters</button></td></tr>}
            {sortedRows.map((r, i) => (
              <tr key={i} className="border-b last:border-b-0 border-tnh-border/40 dark:border-tnh-border-dark/40 hover:bg-tnh-accent/[0.02]">
                <td className="px-4 py-2 text-xs whitespace-nowrap">{r.date}</td>
                <td className="px-4 py-2 text-xs whitespace-nowrap">{r.time}</td>
                <td className="px-4 py-2 text-xs"><span className={`pill ${r.direction === 'Inbound' ? 'pill-ok' : 'pill-warn'}`}>{r.direction}</span></td>
                <td className="px-4 py-2 text-xs whitespace-nowrap">{flowLabel(r.flow)}{r.public_safety ? <span className="ml-1 px-1.5 py-0.5 rounded bg-red-600 text-white text-[10px] font-semibold">911</span> : null}{r.intl ? <span className="ml-1 px-1.5 py-0.5 rounded bg-purple-600 text-white text-[10px] font-semibold">INTL</span> : null}</td>
                <td className="px-4 py-2 text-xs whitespace-nowrap">{outcomeBadge(r.outcome)}</td>
                <td className="px-4 py-2 text-xs">{r.department || '—'}</td>
                <td className="px-4 py-2 font-mono text-xs">{r.caller_number}</td>
                <td className="px-4 py-2 font-mono text-xs">{r.called_number}</td>
                <td className="px-4 py-2 text-xs">{r.user_name}</td>
                <td className="px-4 py-2 text-xs text-gray-500">{r.user_email}</td>
                <td className="px-4 py-2 text-right text-xs whitespace-nowrap">{r.duration}</td>
                <td className="px-4 py-2 text-xs text-gray-500">{r.rate_tier || '—'}</td>
                <td className="px-4 py-2 text-right text-xs whitespace-nowrap font-medium">{money(r.cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-gray-500">{rows.length > 0 ? (activeFilters.length > 0 ? `${sortedRows.length} of ${rows.length} record(s)` : `${rows.length} record(s)`) : ''}</div>
      </>)}
      {mode === 'dashboard' && <CdrDashboard />}
      {mode === 'numbers' && <CdrNumbers />}
      {mode === 'insights' && <CdrInsights />}
      {mode === 'alerts' && <CdrAlerts />}
      {mode === 'cc' && <CdrContactCenter />}
      {mode === 'aiagent' && <CdrAiAgent isAdmin={isAdmin} />}
      {mode === 'status' && <CdrStatus />}
      {mode === 'customreport' && <CdrCustomReport />}
      {mode === 'analytics' && <CdrAnalytics departments={departments} dept={dept} setDept={setDept} />}
      {mode === 'request' && <CdrRequest />}
      {mode === 'audit' && <CdrAudit />}
      {mode === 'statements' && <CdrStatements />}
      {mode === 'exports' && <CdrExports departments={departments} />}
      </div>
    </div>
  );
}
